import React, {
  createRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useSelector } from "react-redux";

import { IAPIState, IPager } from "types";
import { ToolSearchResults, Pager } from "components";

import { toolSearchDataSelector, toolSearchMetaSelector } from "../state";
import { ToolSearchMessage } from "../components";
import { Loader } from "components/loader/Loader";

interface ToolSearchResultsContainerProps {
  setQueryPage: (page: number) => void;
  clearAllFacets?: () => void;
  noResultMessage: string;
}

export const ToolSearchResultsContainer: React.FC<ToolSearchResultsContainerProps> = ({
  setQueryPage,
  clearAllFacets,
  noResultMessage,
}) => {
  const { status, pagination, hasSearched } = useSelector(
    toolSearchMetaSelector
  );
  const results = useSelector(toolSearchDataSelector);

  const handlePageChange = useCallback(
    (page: number) => {
      // change page in URL
      setQueryPage(page);
    },
    [setQueryPage]
  );

  // scroll results into view if results change
  const isFirstScrollRender = useRef(true);
  const scrollRef = createRef<HTMLDivElement>();
  useEffect(() => {
    if (!results) return;
    if (!scrollRef.current) return;

    // keep page scroll position on first render
    // eg. user scrolls down and click on teaser #10
    // user navigates back to overview, he wants to see teaser #10
    if (isFirstScrollRender.current) {
      isFirstScrollRender.current = false;
      return;
    }

    const documentScroll = document.scrollingElement?.scrollTop || 0;

    // don't scroll into view if we are on top of page already
    if (documentScroll < scrollRef.current.offsetTop) return;

    scrollRef.current.scrollIntoView({ behavior: "smooth" });
  }, [results, scrollRef]);

  const pager = useMemo((): IPager => {
    return {
      currentPage: pagination?.page || 1,
      totalPages: Math.ceil(
        (pagination?.total || 0) / (pagination?.amount || 1)
      ),
      onPageChange: handlePageChange,
    };
  }, [pagination, handlePageChange]);

  return (
    <div ref={scrollRef} className="tool-search__results" aria-live="polite">
      <ToolSearchMessage
        nbItems={pagination?.total}
        isError={status === IAPIState.failed}
        hasSearched={hasSearched}
        clearAllFacets={clearAllFacets}
        noResultMessage={noResultMessage}
      />

      {!!results?.length && (
        <ToolSearchResults
          results={results}
          currentPage={pager.currentPage}
          isLoading={status === IAPIState.pending}
        />
      )}
      {!!pager.currentPage && pager.totalPages > 1 && (
        <div className="centered-pager tool-search__pager">
          <Pager {...pager} />
        </div>
      )}

      <div className="tool-search__loader">
        {status === IAPIState.pending && <Loader size="s" />}
      </div>
    </div>
  );
};
